import Constants from 'expo-constants';
import type { FlatTranslationKey } from '@/core/types/i18n';

/** sessionStorage key for ADHA UAE Pass OAuth `state` (separate from pre-ADHA flow). */
export const ADHA_UAE_PASS_OAUTH_STATE_KEY = 'adha_uae_pass_oauth_state';

/** sessionStorage key for raw OAuth callback query params (written by `/uae-pass`). */
export const ADHA_UAE_PASS_PENDING_RESULT_KEY = 'adha_uae_pass_pending_result';

/** Web callback path registered in UAE Pass portal for ADHA journey. */
export const ADHA_UAE_PASS_CALLBACK_PATH = '/uae-pass';

/** Welcome route — user returns here after callback. */
export const ADHA_UAE_PASS_WELCOME_PATH = '/welcome';

/** Native UAE Pass authenticator return hosts (app-to-app). */
export const ADHA_UAE_PASS_SUCCESS_HOST = 'uaePassSuccess';
export const ADHA_UAE_PASS_FAILURE_HOST = 'uaePassFail';

/** Raw query params stashed by `/uae-pass` — resolved on welcome. */
export interface AdhaUaePassRawCallback {
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
}

/** Resolved OAuth outcome used by welcome UI. */
export interface AdhaUaePassPendingResult {
  code?: string;
  /** Resolved label copy for native consumers */
  error?: string;
  /** Translation key for web consumers */
  errorKey?: FlatTranslationKey;
  openLogin: boolean;
}

/**
 * Native-only pending UI after `/uae-pass` WebView (mirrors web welcome):
 * - dialog: UAE Pass redirect errors + ADHA login API `code` → MessageOkDialog
 * - inline: reserved (legacy); prefer dialog for mapped login errors
 */
export type AdhaUaePassNativePendingUi =
  | { kind: 'dialog'; message: string }
  | { kind: 'inline'; message: string };

export interface AdhaUaePassOAuthConfig {
  baseUrl: string;
  clientId: string;
  redirectUri: string;
  scope: string;
  acrValues: string;
}

function getConfigExtra(): Record<string, unknown> | undefined {
  const config =
    Constants.expoConfig ??
    (Constants as { manifest2?: { extra?: { expoClient?: unknown } } }).manifest2
      ?.extra?.expoClient;
  return (config as { extra?: Record<string, unknown> } | null | undefined)?.extra;
}

function firstNonEmpty(...values: Array<string | undefined>): string {
  for (const value of values) {
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }
  return '';
}

/** Official UAE Pass app schemes — must stay in Info.plist even if EAS env is empty. */
export const UAE_PASS_KNOWN_IOS_SCHEMES = ['uaepassstg', 'uaepass'] as const;
/** Official UAE Pass Android packages — must stay in manifest queries. */
export const UAE_PASS_KNOWN_ANDROID_PACKAGES = [
  'ae.uaepass.mainapp.stg',
  'ae.uaepass.mainapp',
] as const;
const UAE_PASS_ON_DEVICE_ACR =
  'urn:digitalid:authentication:flow:mobileondevice';
const UAE_PASS_WEB_ACR =
  'urn:safelayer:tws:policies:authentication:level:low';

function uniqueNonEmpty(values: Array<string | undefined>): string[] {
  return [...new Set(values.map((value) => value?.trim() ?? '').filter(Boolean))];
}

/** `uaepassstg://digitalid-users-ids/` → `uaepassstg` */
export function normalizeUrlSchemeName(raw: string | undefined): string {
  return (raw ?? '')
    .trim()
    .replace(/:\/\/[\s\S]*$/, '')
    .replace(/:\/?$/, '')
    .toLowerCase();
}

export function getUaePassAppSchemeName(): string {
  return normalizeUrlSchemeName(
    readAdhaUaePassEnv(
      'EXPO_PUBLIC_UAE_PASS_SCHEME',
      process.env.EXPO_PUBLIC_UAE_PASS_SCHEME,
    ),
  );
}

export function getUaePassAppSchemeFallbackName(): string {
  return normalizeUrlSchemeName(
    readAdhaUaePassEnv(
      'EXPO_PUBLIC_UAE_PASS_SCHEME_FALLBACK',
      process.env.EXPO_PUBLIC_UAE_PASS_SCHEME_FALLBACK,
    ),
  );
}

export function getUaePassAppSchemeNames(): string[] {
  return uniqueNonEmpty([
    getUaePassAppSchemeName(),
    getUaePassAppSchemeFallbackName(),
    ...UAE_PASS_KNOWN_IOS_SCHEMES,
  ]);
}

export function getUaePassAndroidPackageId(): string {
  return firstNonEmpty(
    readAdhaUaePassEnv(
      'EXPO_PUBLIC_UAE_PASS_ANDROID_PACKAGE_ID',
      process.env.EXPO_PUBLIC_UAE_PASS_ANDROID_PACKAGE_ID,
    ),
    ...UAE_PASS_KNOWN_ANDROID_PACKAGES,
  );
}

export function getUaePassAndroidPackageIds(): string[] {
  return uniqueNonEmpty([
    readAdhaUaePassEnv(
      'EXPO_PUBLIC_UAE_PASS_ANDROID_PACKAGE_ID',
      process.env.EXPO_PUBLIC_UAE_PASS_ANDROID_PACKAGE_ID,
    ),
    ...UAE_PASS_KNOWN_ANDROID_PACKAGES,
  ]);
}

/** ACR used only when the UAE Pass app is installed (app-to-app). */
export function getOnDeviceAdhaAcrValues(): string {
  return firstNonEmpty(
    readAdhaUaePassEnv(
      'EXPO_PUBLIC_ADHA_UAE_PASS_ACR_VALUES_NATIVE',
      process.env.EXPO_PUBLIC_ADHA_UAE_PASS_ACR_VALUES_NATIVE,
    ),
    readAdhaUaePassEnv(
      'EXPO_PUBLIC_UAE_PASS_ACR_VALUES',
      process.env.EXPO_PUBLIC_UAE_PASS_ACR_VALUES,
    ),
    UAE_PASS_ON_DEVICE_ACR,
  );
}

/** ACR used in the in-app WebView when the UAE Pass app is not installed. */
export function getInAppWebAdhaAcrValues(): string {
  return firstNonEmpty(
    readAdhaUaePassEnv(
      'EXPO_PUBLIC_ADHA_UAE_PASS_ACR_VALUES',
      process.env.EXPO_PUBLIC_ADHA_UAE_PASS_ACR_VALUES,
    ),
    readAdhaUaePassEnv(
      'EXPO_PUBLIC_UAE_PASS_ACR_VALUES_WEB',
      process.env.EXPO_PUBLIC_UAE_PASS_ACR_VALUES_WEB,
    ),
    UAE_PASS_WEB_ACR,
  );
}

export function getAdhaAcrValuesForNative(appInstalled: boolean): string {
  if (appInstalled) {
    return firstNonEmpty(getOnDeviceAdhaAcrValues(), getInAppWebAdhaAcrValues());
  }
  return getInAppWebAdhaAcrValues();
}

/**
 * Native ADHA authorize — MOB pair only.
 * Web still uses `getAdhaUaePassOAuthConfig()` (`EXPO_PUBLIC_ADHA_UAE_PASS_*`).
 */
export function getNativeAdhaClientId(): string {
  const clientId = readAdhaUaePassEnv(
    'EXPO_PUBLIC_UAE_PASS_CLIENT_ID_MOB',
    process.env.EXPO_PUBLIC_UAE_PASS_CLIENT_ID_MOB,
  );
  if (!clientId) {
    throw new Error(
      'Native ADHA UAE Pass is not configured. Set EXPO_PUBLIC_UAE_PASS_CLIENT_ID_MOB in .env',
    );
  }
  return clientId;
}

export function getNativeAdhaRedirectUriFromEnv(): string {
  const redirectUri = readAdhaUaePassEnv(
    'EXPO_PUBLIC_UAE_PASS_REDIRECT_URI_MOB',
    process.env.EXPO_PUBLIC_UAE_PASS_REDIRECT_URI_MOB,
  );
  if (!redirectUri) {
    throw new Error(
      'Native ADHA UAE Pass is not configured. Set EXPO_PUBLIC_UAE_PASS_REDIRECT_URI_MOB in .env',
    );
  }
  return redirectUri;
}

export function getAdhaUaePassProviderHost(): string {
  const baseUrl = readAdhaUaePassEnv(
    'EXPO_PUBLIC_ADHA_UAE_PASS_BASE_URL',
    process.env.EXPO_PUBLIC_ADHA_UAE_PASS_BASE_URL,
  );
  try {
    return new URL(baseUrl).hostname;
  } catch {
    return '';
  }
}

/**
 * Prefer `expo.extra` (set in app.config.js at build time).
 * Also read static `process.env.EXPO_PUBLIC_*` so Metro can inline them on web.
 * Dynamic `process.env[key]` does NOT work in the browser bundle.
 */
function readAdhaUaePassEnv(
  extraKey: string,
  staticEnvValue: string | undefined,
): string {
  const extra = getConfigExtra();
  const fromExtra = extra?.[extraKey];
  const fromDynamicEnv = process.env[extraKey];
  // Metro-inlined EXPO_PUBLIC_* must win over native extra: extra is frozen at
  // the last binary build and otherwise blocks .env changes in Expo Dev Client.
  return firstNonEmpty(
    staticEnvValue,
    typeof fromDynamicEnv === 'string' ? fromDynamicEnv : undefined,
    typeof fromExtra === 'string' ? fromExtra : undefined,
  );
}

/**
 * ADHA-only UAE Pass OAuth config — from env only (no code defaults).
 * Keys must be present at **web build/export** time (CI vars), not only on the
 * static host. Server App Settings alone do not inject into the client bundle.
 */
export function getAdhaUaePassOAuthConfig(): AdhaUaePassOAuthConfig {
  const baseUrl = readAdhaUaePassEnv(
    'EXPO_PUBLIC_ADHA_UAE_PASS_BASE_URL',
    process.env.EXPO_PUBLIC_ADHA_UAE_PASS_BASE_URL,
  );
  const clientId = readAdhaUaePassEnv(
    'EXPO_PUBLIC_ADHA_UAE_PASS_CLIENT_ID',
    process.env.EXPO_PUBLIC_ADHA_UAE_PASS_CLIENT_ID,
  );
  const redirectUri = readAdhaUaePassEnv(
    'EXPO_PUBLIC_ADHA_UAE_PASS_REDIRECT_URI',
    process.env.EXPO_PUBLIC_ADHA_UAE_PASS_REDIRECT_URI,
  );
  const scope = readAdhaUaePassEnv(
    'EXPO_PUBLIC_ADHA_UAE_PASS_SCOPE',
    process.env.EXPO_PUBLIC_ADHA_UAE_PASS_SCOPE,
  );
  const acrValues = readAdhaUaePassEnv(
    'EXPO_PUBLIC_ADHA_UAE_PASS_ACR_VALUES',
    process.env.EXPO_PUBLIC_ADHA_UAE_PASS_ACR_VALUES,
  );

  const missing = (
    [
      ['EXPO_PUBLIC_ADHA_UAE_PASS_BASE_URL', baseUrl],
      ['EXPO_PUBLIC_ADHA_UAE_PASS_CLIENT_ID', clientId],
      ['EXPO_PUBLIC_ADHA_UAE_PASS_REDIRECT_URI', redirectUri],
      ['EXPO_PUBLIC_ADHA_UAE_PASS_SCOPE', scope],
      ['EXPO_PUBLIC_ADHA_UAE_PASS_ACR_VALUES', acrValues],
    ] as const
  )
    .filter(([, value]) => !value)
    .map(([key]) => key);

  if (missing.length > 0) {
    throw new Error(
      `ADHA UAE Pass OAuth is not configured. Set in .env: ${missing.join(', ')}`,
    );
  }

  return {
    baseUrl,
    clientId,
    redirectUri,
    scope,
    acrValues,
  };
}
